import { useEffect, useRef, useState } from 'react';
import { useRouteError } from 'react-router-dom';
import { Box, Button, CssBaseline, Typography, useTheme } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';
import { AtlasThemeProvider } from '@diligentcorp/atlas-theme-mui';
import { lensThemeOptions } from '@diligentcorp/atlas-theme-mui/lib/themes/lens';
import { atlasThemeExtensions } from '../../theme/atlasThemeExtensions';
import { muiLocales } from '../../locales/muiLocales';
import { FALLBACK_LNG } from '../../locales';
import LoadingSpinner from '../LoadingSpinner';
import { isChunkLoadError, reloadForChunkError } from '../../helpers/chunkLoadRecovery';

function ErrorContent({ isChunkError }: { isChunkError: boolean }) {
  const { t } = useTranslation();
  const { tokens } = useTheme();

  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: tokens.semantic.color.background.base.value,
        px: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 3,
          maxWidth: 560,
        }}
      >
        <Box
          sx={{
            width: tokens.core.spacing['6'].value,
            height: tokens.core.spacing['6'].value,
            borderRadius: '50%',
            backgroundColor: tokens.semantic.color.status.error.background.value,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ErrorOutlineIcon
            sx={{
              fontSize: tokens.semantic.iconSize.xl.value,
              color: tokens.semantic.color.status.error.default.value,
            }}
          />
        </Box>

        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: tokens.core.fontWeight.semiBold.value,
            color: tokens.semantic.color.type.default.value,
          }}
        >
          {isChunkError
            ? t('route_error.load_title', "This page couldn't load")
            : t('route_error.title', 'Something went wrong')}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: tokens.semantic.font.text.body.fontSize.value,
            lineHeight: tokens.semantic.font.text.body.lineHeight.value,
            color: tokens.semantic.color.type.muted.value,
          }}
        >
          {isChunkError
            ? t(
                'route_error.load_description',
                'The portal may have been updated, or your connection dropped. Reload to try again.',
              )
            : t(
                'route_error.description',
                'An unexpected error stopped this page from loading. Reload to try again, or go back to the home page.',
              )}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            {t('route_error.reload', 'Reload page')}
          </Button>
          <Button variant="outlined" onClick={() => window.location.assign('/')}>
            {t('route_error.home', 'Go to home page')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Root `errorElement`. Renders outside AppShell, so it brings its own theme provider.
 * A stale-chunk error reloads once on its own; anything else gets a recoverable page
 * instead of React Router's developer screen.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const { i18n } = useTranslation();
  const isChunkError = isChunkLoadError(error);
  const [reloading, setReloading] = useState(isChunkError);
  const attempted = useRef(false);

  useEffect(() => {
    // Ref guard: StrictMode runs effects twice, and the second attempt would land inside
    // the cooldown and flash the error page while the first reload is in flight.
    if (attempted.current) return;
    attempted.current = true;
    if (isChunkError && reloadForChunkError()) return;
    setReloading(false);
    console.error('Route error:', error);
  }, [error, isChunkError]);

  return (
    <AtlasThemeProvider
      locales={muiLocales[i18n.language] ?? muiLocales[FALLBACK_LNG]}
      themeOptions={lensThemeOptions}
      themeExtensions={atlasThemeExtensions}
    >
      <CssBaseline />
      {reloading ? <LoadingSpinner /> : <ErrorContent isChunkError={isChunkError} />}
    </AtlasThemeProvider>
  );
}
